import { useState } from "react";
import { Paperclip } from "lucide-react";
import { ImageLightbox, type ImageLightboxPreview } from "../ui/image-lightbox";
import type { CmmdAttachment } from "@cmmd-forge/contracts";

/**
 * Attachments on a CMMD task, images first as thumbnails.
 *
 * A bug report is usually a screenshot, and a filename tells a triager nothing
 * about it — the whole reason a tester attached the image is that it shows
 * something words did not. Rendering it inline is the difference between
 * triaging from the panel and downloading files to find out what you are
 * looking at.
 *
 * `att.url` is a presigned link that expires within the hour. It is used
 * straight from the fetch that produced it and never persisted; the panel
 * refetches when it reopens, which is what keeps these from going stale.
 */

/**
 * Image types Chromium can actually decode.
 *
 * Deliberately an allowlist rather than `mimeType.startsWith("image/")`. iOS
 * ships HEIC by default, so TestFlight screenshots arrive as `image/heic` more
 * often than not, and Chromium cannot decode it — a prefix check would both
 * render a broken `<img>` AND remove the file from the list below it, leaving
 * no way to open the one attachment the tester cared about. Anything not on
 * this list falls through to the file row, which always works.
 */
const BROWSER_DECODABLE_IMAGE_TYPES: ReadonlySet<string> = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/webp",
  "image/avif",
  "image/bmp",
  "image/svg+xml",
]);

/**
 * CMMD signs download URLs per request and returns `null` when signing fails,
 * so one unsignable object does not fail the whole list. That means a row can
 * legitimately arrive with no URL — rendering it as an `href`/`src` anyway
 * produces a dead link and a broken image, which reads as data loss rather than
 * as a transient storage hiccup.
 */
function hasUrl(att: CmmdAttachment): att is CmmdAttachment & { readonly url: string } {
  return typeof att.url === "string" && att.url.length > 0;
}

/**
 * Narrowing predicate, not a boolean: it carries the non-null `url` through
 * `.filter()` so the thumbnail block does not have to re-assert what the
 * classification already proved.
 */
function isThumbnailable(att: CmmdAttachment): att is CmmdAttachment & { readonly url: string } {
  return hasUrl(att) && BROWSER_DECODABLE_IMAGE_TYPES.has((att.mimeType ?? "").toLowerCase());
}

function formatAttachmentSize(sizeBytes: number | null): string | null {
  if (sizeBytes === null) return null;
  if (sizeBytes < 1024) return `${sizeBytes}B`;
  if (sizeBytes < 1024 * 1024) return `${Math.round(sizeBytes / 1024)}KB`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)}MB`;
}

interface AttachmentSizeProps {
  readonly sizeBytes: number | null;
}

function AttachmentSize({ sizeBytes }: AttachmentSizeProps) {
  const label = formatAttachmentSize(sizeBytes);
  return label === null ? null : <span className="shrink-0 text-muted-foreground/40">{label}</span>;
}

interface TaskAttachmentListProps {
  readonly attachments: ReadonlyArray<CmmdAttachment>;
}

export function TaskAttachmentList({ attachments }: TaskAttachmentListProps) {
  const images = attachments.filter(isThumbnailable);
  const files = attachments.filter((att) => !isThumbnailable(att));
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  // Every thumbnailable attachment, so the lightbox can page through the set
  // rather than trapping the viewer on whichever one they happened to click.
  const preview: ImageLightboxPreview | null =
    openIndex === null
      ? null
      : {
          images: images.map((att) => ({
            src: att.url,
            name: att.filename,
            sizeBytes: att.sizeBytes,
          })),
          index: openIndex,
        };

  return (
    <div className="flex flex-col gap-3">
      {images.length > 0 ? (
        <ul className="grid grid-cols-2 gap-2">
          {images.map((att, index) => (
            <li key={att.id}>
              <button
                type="button"
                title={att.filename}
                onClick={() => setOpenIndex(index)}
                className="group block w-full cursor-zoom-in overflow-hidden rounded-md border border-border/45 text-left transition-colors hover:border-border/70"
              >
                {/*
                  Square, so a grid of screenshots scans as an even set. Phone
                  screenshots are tall and portrait; letting each thumbnail take
                  its own aspect gave a ragged grid where the tallest image
                  dictated the row.
                */}
                <img
                  src={att.url}
                  alt={att.filename}
                  loading="lazy"
                  className="aspect-square w-full bg-muted/20 object-cover"
                />
                <div className="flex items-center gap-2 px-2 py-1.5 text-[11px] text-muted-foreground/70 transition-colors group-hover:text-foreground">
                  <span className="min-w-0 flex-1 truncate">{att.filename}</span>
                  <AttachmentSize sizeBytes={att.sizeBytes} />
                </div>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {files.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {files.map((att) => (
            <li key={att.id}>
              {hasUrl(att) ? (
                <a
                  href={att.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 rounded-md border border-border/45 px-2.5 py-2 text-xs text-muted-foreground/75 transition-colors hover:border-border/70 hover:bg-muted/20 hover:text-foreground"
                >
                  <Paperclip className="size-3.5 shrink-0" aria-hidden="true" />
                  <span className="min-w-0 flex-1 truncate">{att.filename}</span>
                  <AttachmentSize sizeBytes={att.sizeBytes} />
                </a>
              ) : (
                // Still listed, deliberately: the attachment exists and the
                // tester's file is not lost. Silently dropping the row would
                // read as data loss; a dead link would read as a bug.
                <div
                  className="flex items-center gap-2 rounded-md border border-dashed border-border/45 px-2.5 py-2 text-xs text-muted-foreground/50"
                  title="This attachment could not be signed for download. Reopen the task to retry."
                >
                  <Paperclip className="size-3.5 shrink-0" aria-hidden="true" />
                  <span className="min-w-0 flex-1 truncate">{att.filename}</span>
                  <span className="shrink-0">unavailable</span>
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : null}

      {preview === null ? null : (
        <ImageLightbox preview={preview} onClose={() => setOpenIndex(null)} />
      )}
    </div>
  );
}
